import { useEffect, useState } from 'react';

import { garmentOutline, garmentOutlines } from '@core/bookings/outline';

/**
 * A design's picture at the catalogue's shape, 4:5 — or, with none, a clean outline of what it is.
 *
 * <p>
 * <strong>The outline is chosen from the category</strong> (`@core/bookings/outline`), so a design
 * nobody has photographed still reads as a sherwani or a safa at a glance, and a half-photographed
 * catalogue looks finished rather than broken. A link that has expired or will not load falls back
 * to the same outline — to the person looking, no picture and a dead link are the same thing, which
 * is `GarmentImage`'s rule too.
 * </p>
 *
 * <p>
 * Whatever is drawn over the corner (an availability badge) is the caller's, passed as children.
 * </p>
 */
export function DesignPhoto({
  url,
  alt,
  categoryPath,
  className = '',
  children,
}: {
  url: string | null | undefined;
  alt: string;
  categoryPath: string;
  className?: string;
  children?: React.ReactNode;
}) {
  const [failed, setFailed] = useState(false);

  // Keyed on the link: a refetch after expiry hands over a fresh one, and a failure remembered from
  // the old link would keep the outline up over a picture that now works.
  useEffect(() => {
    setFailed(false);
  }, [url]);

  const showing = url && !failed;
  const shape = garmentOutlines[garmentOutline(categoryPath)];

  return (
    <div className={`relative aspect-[4/5] w-full overflow-hidden bg-light ${className}`}>
      {showing ? (
        <img
          src={url}
          alt={alt}
          loading="lazy"
          onError={() => setFailed(true)}
          className="size-full object-cover"
        />
      ) : (
        <div
          role="img"
          aria-label={`No photograph of ${alt} yet`}
          className="text-primary bg-primary-transparent flex size-full items-center justify-center"
        >
          <svg viewBox={shape.viewBox} className="h-[58%] w-auto opacity-70" aria-hidden="true">
            {shape.fill.map((d) => (
              <path key={d} d={d} fill="currentColor" />
            ))}
            <path
              d={shape.detail}
              stroke="#fff"
              strokeOpacity={0.55}
              strokeWidth={1.7}
              fill="none"
            />
          </svg>
        </div>
      )}

      {children}
    </div>
  );
}
